import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1769913995907-e1cc7ff527a1?w=1600&q=85";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Lake" className="absolute inset-0 h-full w-full object-cover"/>
        <div className="absolute inset-0 bg-primary/30"/>
        <div className="relative p-12 text-white h-full flex items-end">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] mb-3 text-white/85">SlipShare</div>
            <p className="font-display text-3xl max-w-md leading-tight">"A slip on Lake Norman for less than storage. Best decision this summer."</p>
            <p className="text-sm mt-4 text-white/80">— Cole, boat owner</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm">
          <h1 className="font-display text-4xl mb-2">Welcome back</h1>
          <p className="text-muted-foreground mb-8">Sign in to manage your bookings and slips.</p>

          <Button type="button" data-testid="google-signin-btn" variant="outline" className="w-full mb-4 gap-2" onClick={googleLogin}>
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.6c2.1-1.9 3.2-4.7 3.2-8.1z"/><path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.7c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.2-1.9-6-4.4H2.3v2.8C4.1 20.5 7.8 23 12 23z"/><path fill="#FBBC05" d="M6 14.4c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.6H2.3C1.5 9 1 10.7 1 12.4s.5 3.4 1.3 4.8L6 14.4z"/><path fill="#EA4335" d="M12 5.5c1.6 0 3 .5 4.1 1.5l3.1-3.1C17.4 2.1 14.9 1 12 1 7.8 1 4.1 3.5 2.3 7.6L6 10.4C6.8 7.9 9.2 5.5 12 5.5z"/></svg>
            Continue with Google
          </Button>
          <div className="relative my-4 text-center text-xs text-muted-foreground">
            <span className="bg-background px-2 relative z-10">or with email</span>
            <div className="absolute inset-x-0 top-1/2 border-t border-border"/>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}/>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}/>
            </div>
          </div>
          <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full mt-6 bg-primary hover:bg-primary/90">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <p className="mt-6 text-sm text-muted-foreground text-center">
            No account? <Link to="/register" data-testid="link-register" className="text-primary link-underline">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
